import { useMutation, useQuery, useQueryClient } from 'react-query';
import axios from 'axios';
import { predictionArbitrageManagerAxiosApi } from 'src/Config/axiosApi';

/* ------------------------------------------------------------------ */
/* Types                                                               */
/* ------------------------------------------------------------------ */

export type Resolution = 'yes' | 'no' | 'unresolved';
export type LegSide = 'yes' | 'no' | 'unconfirmed' | 'none';
export type HedgeClass = 'CALZADA' | 'DESCALZADA' | 'NAKED_TOTAL';
export type ResolutionSource =
  | 'kalshi_result'
  | 'kalshi_settlement'
  | 'polymarket_gamma'
  | 'price_band';

export interface FinishLeg {
  side: LegSide;
  contracts: number;
  avgPrice: number | null;
  feesUsd: number;
  netUsd: number | null;
  settled: boolean;
}

export interface FinishPolyLeg {
  side: LegSide;
  contracts: number;
  avgPrice: number | null;
  netUsd: number | null;
  redeemed: boolean;
}

export interface FinishHedge {
  class: HedgeClass;
  coreUsd: number;
  nakedUsd: number;
  nakedContracts: number;
}

export interface FinishMarket {
  pairId: string;
  pairName: string;
  kalshiTicker: string | null;
  polymarketConditionId: string | null;
  resolution: Resolution;
  resolutionSource: ResolutionSource;
  kalshiLeg: FinishLeg;
  polymarketLeg: FinishPolyLeg;
  netUsd: number | null;
  hedge: FinishHedge;
  flags: string[];
}

export interface FinishTotals {
  groupNetUsd: number | null;
  kalshiNetUsd: number;
  polymarketNetUsd: number;
  polyUnredeemedCash: number;
  realizedCashUsd: number | null;
  winnersUsd: number;
  losersUsd: number;
  coreUsd: number;
  nakedUsd: number;
}

export interface FinishCounts {
  pairsInGroup: number;
  marketsWithFills: number;
  marketsNeverFilled: number;
  unresolvedMarkets: number;
  polymarketUnredeemedMarkets: number;
  unconfirmedLegs: number;
}

export interface FinishAnomalies {
  nakedRemainder: number;
  counterStuck: number;
  hedgeDeferred: number;
  errors: number;
  panics: number;
  tradingHalted: number;
  duplicateRules: number;
}

export interface FinishConfig {
  winThreshold: number;
  loseThreshold: number;
  resolutionSourcePriority: string[];
}

export interface FinishSnapshot {
  _id: string;
  groupId: string;
  groupName: string;
  finishedAt: string;
  finishedByUserId?: string;
  config: FinishConfig;
  totals: FinishTotals;
  counts: FinishCounts;
  markets: FinishMarket[];
  anomalies: FinishAnomalies;
  createdAt: string;
}

export interface FinishResultSummary {
  groupId: string;
  groupName: string;
  finishedAt: string;
  groupNetUsd: number | null;
  kalshiNetUsd: number;
  polymarketNetUsd: number;
  polyUnredeemedCash: number;
  unresolvedMarkets: number;
  polymarketUnredeemedMarkets: number;
}

export interface FinishResultsPage {
  items: FinishResultSummary[];
  total: number;
  page: number;
  limit: number;
}

/* ------------------------------------------------------------------ */
/* Query keys                                                          */
/* ------------------------------------------------------------------ */

export const groupFinishQueryKey = (groupId: string) => ['group-finish', groupId];
export const FINISH_RESULTS_KEY = ['finish-results'];

/* ------------------------------------------------------------------ */
/* Request functions (exported for wire-shape tests)                   */
/* ------------------------------------------------------------------ */

export async function finishGroup(
  groupId: string,
  body?: { winThreshold?: number; loseThreshold?: number }
): Promise<FinishSnapshot> {
  const { data } = await predictionArbitrageManagerAxiosApi.post<FinishSnapshot>(
    `/groups/${groupId}/finish`,
    body ?? {}
  );
  return data;
}

export async function fetchGroupFinishLatest(
  groupId: string
): Promise<FinishSnapshot | null> {
  try {
    const { data } =
      await predictionArbitrageManagerAxiosApi.get<FinishSnapshot>(
        `/groups/${groupId}/finish`
      );
    return data;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response?.status === 404) {
      return null;
    }
    throw err;
  }
}

export async function fetchFinishResultsList(
  page: number,
  limit: number
): Promise<FinishResultsPage> {
  const { data } =
    await predictionArbitrageManagerAxiosApi.get<FinishResultsPage>(
      '/finish/results',
      { params: { page, limit } }
    );
  return data;
}

export async function fetchFinishResultDetail(
  groupId: string,
  snapshotId?: string
): Promise<FinishSnapshot | null> {
  try {
    const { data } =
      await predictionArbitrageManagerAxiosApi.get<FinishSnapshot>(
        `/finish/results/${groupId}`,
        snapshotId ? { params: { snapshotId } } : undefined
      );
    return data;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response?.status === 404) {
      return null;
    }
    throw err;
  }
}

/* ------------------------------------------------------------------ */
/* Mutations                                                           */
/* ------------------------------------------------------------------ */

/**
 * POST /groups/${groupId}/finish
 * Body: { winThreshold?, loseThreshold? }
 * On success: invalidate the group's finish snapshot + the results list.
 */
export function useFinishGroup(groupId: string) {
  const qc = useQueryClient();
  return useMutation(
    (body?: { winThreshold?: number; loseThreshold?: number }) =>
      finishGroup(groupId, body),
    {
      mutationKey: ['entry:group-finish', groupId],
      onSuccess: () => {
        qc.invalidateQueries(groupFinishQueryKey(groupId));
        qc.invalidateQueries(FINISH_RESULTS_KEY);
      }
    }
  );
}

/* ------------------------------------------------------------------ */
/* Queries                                                             */
/* ------------------------------------------------------------------ */

/**
 * GET /groups/${groupId}/finish
 * Returns the latest FinishSnapshot for the group, or null if none exists (404).
 */
export function useGroupFinishLatest(groupId: string) {
  return useQuery<FinishSnapshot | null>(
    groupFinishQueryKey(groupId),
    () => fetchGroupFinishLatest(groupId),
    {
      enabled: groupId.length > 0,
      staleTime: 30_000,
      keepPreviousData: true
    }
  );
}

/**
 * GET /finish/results?page=&limit=
 * Returns a paginated list of FinishResultSummary across all groups.
 */
export function useFinishResultsList(page: number, limit: number) {
  return useQuery<FinishResultsPage>(
    [...FINISH_RESULTS_KEY, page, limit],
    () => fetchFinishResultsList(page, limit),
    {
      keepPreviousData: true,
      staleTime: 60_000
    }
  );
}

/**
 * GET /finish/results/${groupId}?snapshotId=...
 * Returns a specific FinishSnapshot (or the latest for the group), or null on 404.
 */
export function useFinishResultDetail(groupId: string, snapshotId?: string) {
  return useQuery<FinishSnapshot | null>(
    ['finish-result-detail', groupId, snapshotId ?? null],
    () => fetchFinishResultDetail(groupId, snapshotId),
    {
      enabled: groupId.length > 0,
      staleTime: 30_000
    }
  );
}
